import {
  Position,
  Direction,
  PlacedWord,
  GameGrid,
  GridCell,
  ALL_DIRECTIONS,
  GRID_SIZE,
  isInBounds,
  positionsEqual
} from '../types';
import { getRandomWords } from './wordBank';

/**
 * Creates an empty grid with null cells
 */
function createEmptyGrid(size: number): (string | null)[][] {
  return Array(size).fill(null).map(() => Array(size).fill(null));
}

/**
 * Check if a word can be placed at the given position with the given direction
 */
function canPlaceWord(
  grid: (string | null)[][],
  word: string,
  startPos: Position,
  direction: Direction
): boolean {
  let pos = { ...startPos };
  
  for (let i = 0; i < word.length; i++) {
    if (!isInBounds(pos, grid.length)) {
      return false;
    }
    
    const currentCell = grid[pos.row][pos.col];
    // Cell must be empty - no sharing allowed
    if (currentCell !== null) {
      return false;
    }
    
    pos = {
      row: pos.row + direction.dRow,
      col: pos.col + direction.dCol
    };
  }
  
  return true;
}

/**
 * Place a word on the grid
 */
function placeWord(
  grid: (string | null)[][],
  word: string,
  startPos: Position,
  direction: Direction
): Position[] {
  const path: Position[] = [];
  let pos = { ...startPos };
  
  for (let i = 0; i < word.length; i++) {
    grid[pos.row][pos.col] = word[i];
    path.push({ ...pos });
    
    pos = {
      row: pos.row + direction.dRow,
      col: pos.col + direction.dCol
    };
  }
  
  return path;
}

/**
 * Generate possible zig-zag path for a word
 * Zig-zag alternates between two compatible directions
 */
function generateZigZagPath(
  grid: (string | null)[][],
  word: string,
  startPos: Position
): Position[] | null {
  // Try various zig-zag patterns
  const zigZagPatterns: [Direction, Direction][] = [
    // Horizontal with vertical zig
    [{ dRow: 0, dCol: 1 }, { dRow: 1, dCol: 1 }],   // right, down-right
    [{ dRow: 0, dCol: 1 }, { dRow: -1, dCol: 1 }],  // right, up-right
    [{ dRow: 0, dCol: -1 }, { dRow: 1, dCol: -1 }], // left, down-left
    [{ dRow: 0, dCol: -1 }, { dRow: -1, dCol: -1 }], // left, up-left
    // Vertical with horizontal zig
    [{ dRow: 1, dCol: 0 }, { dRow: 1, dCol: 1 }],   // down, down-right
    [{ dRow: 1, dCol: 0 }, { dRow: 1, dCol: -1 }],  // down, down-left
    [{ dRow: -1, dCol: 0 }, { dRow: -1, dCol: 1 }], // up, up-right
    [{ dRow: -1, dCol: 0 }, { dRow: -1, dCol: -1 }], // up, up-left
    // Diagonal zig-zags
    [{ dRow: 1, dCol: 1 }, { dRow: 0, dCol: 1 }],   // down-right, right
    [{ dRow: 1, dCol: 1 }, { dRow: 1, dCol: 0 }],   // down-right, down
    [{ dRow: -1, dCol: -1 }, { dRow: 0, dCol: -1 }], // up-left, left
    [{ dRow: -1, dCol: -1 }, { dRow: -1, dCol: 0 }], // up-left, up
  ];
  
  // Shuffle patterns for variety
  const shuffledPatterns = [...zigZagPatterns].sort(() => Math.random() - 0.5);
  
  for (const [dir1, dir2] of shuffledPatterns) {
    const path = tryZigZagPlacement(grid, word, startPos, dir1, dir2);
    if (path) {
      return path;
    }
  }
  
  return null;
}

/**
 * Try to place word in zig-zag pattern alternating between two directions
 */
function tryZigZagPlacement(
  grid: (string | null)[][],
  word: string,
  startPos: Position,
  dir1: Direction,
  dir2: Direction
): Position[] | null {
  const path: Position[] = [];
  let pos = { ...startPos };
  let useFirstDir = true;
  
  for (let i = 0; i < word.length; i++) {
    if (!isInBounds(pos, grid.length)) {
      return null;
    }
    
    if (grid[pos.row][pos.col] !== null) {
      return null;
    }
    
    path.push({ ...pos });
    
    if (i < word.length - 1) {
      const dir = useFirstDir ? dir1 : dir2;
      pos = {
        row: pos.row + dir.dRow,
        col: pos.col + dir.dCol
      };
      useFirstDir = !useFirstDir;
    }
  }
  
  return path;
}

/**
 * Place a word using a zig-zag path
 */
function placeWordWithPath(
  grid: (string | null)[][],
  word: string,
  path: Position[]
): void {
  for (let i = 0; i < word.length; i++) {
    grid[path[i].row][path[i].col] = word[i];
  }
}

/**
 * Try to place a word on the grid using any valid method
 * Returns the path if successful, null otherwise
 */
function tryPlaceWordAnywhere(
  grid: (string | null)[][],
  word: string
): Position[] | null {
  const size = grid.length;
  const positions: Position[] = [];
  
  // Generate all possible starting positions
  for (let row = 0; row < size; row++) {
    for (let col = 0; col < size; col++) {
      positions.push({ row, col });
    }
  }
  
  // Shuffle positions for randomness
  positions.sort(() => Math.random() - 0.5);
  
  // Shuffle directions
  const directions = [...ALL_DIRECTIONS].sort(() => Math.random() - 0.5);
  
  // Try each position with each direction
  for (const pos of positions) {
    // 70% chance to try straight placement, 30% for zig-zag
    if (Math.random() < 0.7) {
      for (const dir of directions) {
        if (canPlaceWord(grid, word, pos, dir)) {
          return placeWord(grid, word, pos, dir);
        }
      }
    }
    
    // Try zig-zag placement
    const zigZagPath = generateZigZagPath(grid, word, pos);
    if (zigZagPath) {
      placeWordWithPath(grid, word, zigZagPath);
      return zigZagPath;
    }
    
    // If zig-zag didn't work, try straight placement
    for (const dir of directions) {
      if (canPlaceWord(grid, word, pos, dir)) {
        return placeWord(grid, word, pos, dir);
      }
    }
  }
  
  return null;
}

/**
 * Fill remaining empty cells with random letters
 */
function fillEmptyCells(grid: (string | null)[][]): void {
  const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  
  for (let row = 0; row < grid.length; row++) {
    for (let col = 0; col < grid[row].length; col++) {
      if (grid[row][col] === null) {
        grid[row][col] = letters[Math.floor(Math.random() * letters.length)];
      }
    }
  }
}

/**
 * Convert raw grid to GameGrid with metadata
 */
function createGameGrid(
  rawGrid: (string | null)[][],
  placedWords: PlacedWord[]
): GameGrid {
  const cells: GridCell[][] = [];
  
  for (let row = 0; row < rawGrid.length; row++) {
    cells[row] = [];
    for (let col = 0; col < rawGrid[row].length; col++) {
      const position: Position = { row, col };
      
      // Check if this cell is part of any word
      let isPartOfWord = false;
      let wordIndex: number | undefined;
      
      for (let i = 0; i < placedWords.length; i++) {
        if (placedWords[i].path.some(p => positionsEqual(p, position))) {
          isPartOfWord = true;
          wordIndex = i;
          break;
        }
      }
      
      cells[row][col] = {
        letter: rawGrid[row][col] || '',
        position,
        isPartOfWord,
        wordIndex
      };
    }
  }
  
  return {
    cells,
    size: rawGrid.length
  };
}

/**
 * Generate a complete game grid with hidden words
 * @param wordCount Number of words to place (default 10)
 * @param maxAttempts Maximum generation attempts before giving up
 */
export function generateGameGrid(
  wordCount: number = 10,
  maxAttempts: number = 50
): { grid: GameGrid; placedWords: PlacedWord[] } | null {
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const grid = createEmptyGrid(GRID_SIZE);
    const placedWords: PlacedWord[] = [];
    
    // Get random words, sorted by length (longer first for better placement)
    const words = getRandomWords(wordCount + 5, 4, 7) // Get extras in case some fail
      .sort((a, b) => b.length - a.length);
    
    let placedCount = 0;
    
    for (const word of words) {
      if (placedCount >= wordCount) break;
      
      const path = tryPlaceWordAnywhere(grid, word);
      if (path) {
        placedWords.push({ word, path });
        placedCount++;
      }
    }
    
    if (placedCount === wordCount) {
      // Successfully placed all words
      fillEmptyCells(grid);
      return {
        grid: createGameGrid(grid, placedWords),
        placedWords
      };
    }
  }
  
  // Failed to generate a valid grid
  return null;
}

/**
 * Check if a selection matches any of the hidden words
 * Supports both forward and backward matching
 */
export function checkWordMatch(
  selection: string,
  hiddenWords: PlacedWord[],
  foundWords: string[]
): PlacedWord | null {
  const reversedSelection = selection.split('').reverse().join('');
  
  for (const placedWord of hiddenWords) {
    // Skip already found words
    if (foundWords.includes(placedWord.word)) {
      continue;
    }
    
    // Check both forward and backward
    if (placedWord.word === selection || placedWord.word === reversedSelection) {
      return placedWord;
    }
  }
  
  return null;
}

/**
 * Check if a path matches a placed word's path (in either direction)
 */
export function doesPathMatchWord(
  selectionPath: Position[],
  wordPath: Position[]
): boolean {
  if (selectionPath.length !== wordPath.length) {
    return false;
  }
  
  // Check forward match
  const forwardMatch = selectionPath.every((pos, i) => 
    positionsEqual(pos, wordPath[i])
  );
  
  if (forwardMatch) return true;
  
  // Check backward match
  const reversedWordPath = [...wordPath].reverse();
  const backwardMatch = selectionPath.every((pos, i) => 
    positionsEqual(pos, reversedWordPath[i])
  );
  
  return backwardMatch;
}
